import { select, fork, put, take, race } from "redux-saga/effects";
import moment from "moment";
import { REHYDRATE } from "redux-persist";
import {
  getShouldRequestIssues,
  setShouldRequestIssues,
} from "./requestSchedule";
import { LOGOUT_SUCCESS } from "../auth/actionTypes";
import { APP_SETUP_COMPLETE } from "../appSetup/actionTypes";
import { matchMDLinks, matchUnSearchable } from "../../../utils/regex";

const initialState = {
  showClosed: false,
  loading: false,
  loaded: false,
  siteHasChanged: true,
  data: [],
};

// Utils
function stripSpecialChars(str = "") {
  // NOTE: flexsearch doesn't like excessive special characters, like those found in SS links
  const stripped = str
    // Remove markdown link urls
    .replace(matchMDLinks, "$1")
    // Remove all non-alpha numeric chars (allows underscore)
    .replace(matchUnSearchable, "");
  return stripped;
}

function composeSearchString(fields = []) {
  return fields.reduce(
    (searchable, field) =>
      field !== undefined ? `${searchable} ${field}` : searchable,
    ""
  );
}

function flattenEquipFields(equips = []) {
  let equipFields = "";
  if (equips.length > 0) {
    equipFields = equips.reduce(
      (fields, equip) =>
        `${fields} ${equip.targetName} ${equip.ruleName} ${equip.description}`,
      ""
    );
  }

  return equipFields;
}

function composeIssueData(data = []) {
  return data.map((issue, i) => {
    const searchable = composeSearchString([
      issue.components,
      issue.insite_assignee,
      issue.bonfire_status,
      issue.context,
      issue.description,
      issue.priority,
      issue.responsible,
      issue.spark_type,
      issue.summary,
      issue.key,
      issue.project_name,
      issue.transition_reason,
      flattenEquipFields(issue.associatedEquips),
    ]);

    const valueString = stripSpecialChars(searchable);

    return {
      ...issue,
      searchId: i,
      valueString,
    };
  });
}

export const CLOSED_ACTIONS_START_DATE = {
  LAST_12_MONTHS: 12,
  LAST_24_MONTHS: 24,
  ALL_TIME: -1,
};

const getClosedActionStartDate = (
  closedActionsStartDate = CLOSED_ACTIONS_START_DATE.LAST_12_MONTHS
) => {
  if (closedActionsStartDate === -1) {
    return moment("2016-01-01").toJSON();
  }
  return moment().subtract(closedActionsStartDate, "months").toJSON();
};

/**
 * Update the `updated` property of an issue, by
 * jira id.
 *
 * @param issues array of actions/issues
 * @param id issue jira key
 * @param updated UTC timestamp
 */
function refreshUpdatedIssue(issues, id, updated) {
  return issues.map((i) => {
    if (i.key === id) {
      return {
        ...i,
        updated,
      };
    }
    return i;
  });
}

// Loading the list of issues
export const LOAD_BETA_ISSUES = "bonfire/issues/LOAD_BETA_ISSUES";
export const LOAD_BETA_ISSUES_REQUEST =
  "bonfire/issues/LOAD_BETA_ISSUES_REQUEST";
export const LOAD_BETA_ISSUES_REQUEST_SUCCESS =
  "bonfire/issues/LOAD_BETA_ISSUES_REQUEST_SUCCESS";
export const LOAD_BETA_ISSUES_REQUEST_FAIL =
  "bonfire/issues/LOAD_BETA_ISSUES_REQUEST_FAIL";
export const UPDATE_BETA_ISSUE = "bonfire/issues/UPDATE_BETA_ISSUE";

/**
 * ACTION CREATORS: CLIENT PROMISES
 */
export const loadIssuesRequest = (status, closedActionsStartDate = 12) => ({
  type: LOAD_BETA_ISSUES,
  status,
  closedActionsStartDate,
});
export const updateBetaIssue = (id, updated) => ({
  type: UPDATE_BETA_ISSUE,
  id,
  updated,
});

export const loadIssues = (user, site, project, status = "pending") => ({
  types: [
    LOAD_BETA_ISSUES_REQUEST,
    LOAD_BETA_ISSUES_REQUEST_SUCCESS,
    LOAD_BETA_ISSUES_REQUEST_FAIL,
  ],
  promise: (client) =>
    client.post("/issues", {
      headers: {
        Authorization: `Bearer ${user.token}`,
      },
      data: {
        site,
        project,
        status,
      },
    }),
});

export const loadClosedIssues = (user, site, project, from) => ({
  types: [
    LOAD_BETA_ISSUES_REQUEST,
    LOAD_BETA_ISSUES_REQUEST_SUCCESS,
    LOAD_BETA_ISSUES_REQUEST_FAIL,
  ],
  promise: (client) =>
    client.post("/issues", {
      headers: {
        Authorization: `Bearer ${user.token}`,
      },
      data: {
        target: "",
        site,
        project,
        status: "closed",
        closedOnStartDate: from,
        closedOnEndDate: moment().toJSON(),
      },
    }),
});

export function reducer(state = initialState, action = {}) {
  switch (action.type) {
    case LOAD_BETA_ISSUES_REQUEST:
      return {
        ...state,
        loading: true,
        loaded: false,
      };
    case LOAD_BETA_ISSUES_REQUEST_SUCCESS:
      return {
        ...state,
        loading: false,
        loaded: true,
        data: composeIssueData(action.result),
        siteHasChanged: false,
        error: undefined,
      };
    case LOAD_BETA_ISSUES_REQUEST_FAIL:
      return {
        ...state,
        data: [],
        loaded: false,
        loading: false,
        siteHasChanged: false,
        error: action.error,
      };
    case UPDATE_BETA_ISSUE:
      return {
        ...state,
        data: refreshUpdatedIssue(state.data, action.id, action.updated),
      };
    default:
      return state;
  }
}

export const ACTION_STATUSES = {
  open: "open",
  closed: "closed",
};

export const getAuthUser = (state) => state.auth.user;
export const getBetaIssues = (state) => state.betaIssues;
export const getUserSites = (state) => state.userSites;
export const getBetaSelections = (state) => state.betaSelections;
export const getAppSetup = (state) => state.appSetup;

export function* loadIssuesFlow() {
  while (true) {
    // Check global requestHanlder flag to see if we should load issues
    const { status, closedActionsStartDate } = yield take(LOAD_BETA_ISSUES);
    const shouldRequestIssues = yield select(getShouldRequestIssues);
    const { loading } = yield select(getBetaIssues);

    // If app isn't ready, wait for it to be.
    const { appSetupComplete } = yield select(getAppSetup);
    if (!appSetupComplete) {
      yield take(APP_SETUP_COMPLETE);
    }

    if (shouldRequestIssues && !loading) {
      try {
        const showClosed = status === ACTION_STATUSES.closed;
        const user = yield select(getAuthUser);
        const { sites: userSites, projects: userProjects } = yield select(
          getUserSites
        );
        const { selectedSites: syncoIds } = yield select(getBetaSelections);

        const payload = syncoIds.reduce(
          (siteProjectPayload, syncoId) => {
            const site = userSites[syncoId];
            const project = userProjects[site.projectId];
            siteProjectPayload.sites.add(site.id);
            siteProjectPayload.projects.add(project.jira_key);
            return siteProjectPayload;
          },
          { sites: new Set(), projects: new Set() }
        );

        if (!user.rolesRestriction) throw Error("Unable to authenticate user");

        // Request closed issues
        if (showClosed) {
          const from = getClosedActionStartDate(closedActionsStartDate);
          yield put(
            loadClosedIssues(
              user,
              Array.from(payload.sites),
              Array.from(payload.projects),
              from
            )
          );
        } else {
          // 'open' status will request all not-closed issues, meaning it'll include 'open' Jira issues
          // 'pending' is the default user status, which excludes 'open' jira issues (as these aren't ready for clients yet)
          // NOTE: Captain validates access if `open` status is requested
          const { includeOpenStatus } = yield select(
            (state) => state.betaActionsUI
          );
          const statusFilter =
            user.rolesRestriction.includes("Project Admins") &&
            includeOpenStatus
              ? "open"
              : "pending";
          // Fire off the load issues request
          yield put(
            loadIssues(
              user,
              Array.from(payload.sites),
              Array.from(payload.projects),
              statusFilter
            )
          );
        }
      } catch (err) {
        console.error(`Something went wrong attempting to load issues: ${err}`);
      }
    }

    yield race({
      fail: take(LOAD_BETA_ISSUES_REQUEST_FAIL),
      success: take(LOAD_BETA_ISSUES_REQUEST_SUCCESS),
    });

    yield put(setShouldRequestIssues(false));
  }
}

// TODO: Refactor this flow to be shared, as it's mostly duplicated across rules.js and issues.js
export function* issuesFlow() {
  yield take(REHYDRATE);

  while (true) {
    const loadIssuesTask = yield fork(loadIssuesFlow);

    yield take(LOGOUT_SUCCESS);

    yield loadIssuesTask.cancel();
  }
}
